import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt } from './_finHelpers';

// Percent Off Calculator — instant discount / sale-price / savings solver.
// Supports quick-pick % chips (10, 20, 25, 30, 40, 50, 60, 70, 75) that
// snap the discount and auto-update everything in real time.
export default function PercentOffCalc() {
  const [original, setOriginal] = useState(100);
  const [pct, setPct] = useState(20);

  const c = useMemo(() => {
    const savings = original * pct / 100;
    const sale = original - savings;
    return { savings, sale };
  }, [original, pct]);

  const chips = [10, 20, 25, 30, 40, 50, 60, 70, 75];

  return (
    <CalcShell showActions={false} look="cnet" title="Percent Off Calculator"
      subtitle="Instantly calculate the sale price, savings, and effective percent-off on any purchase."
      about={<>
        <p>The <b>Percent Off Calculator</b> quickly shows how much you save on a sale item. Enter the original price and the discount percentage; the sale price and savings amount are computed automatically.</p>
        <p>Formula: <b>Sale Price = Original Price × (1 − Discount%/100)</b>. Also known as: <i>discount calculator</i>, <i>sale price calculator</i>, <i>discount rebate calculator</i>.</p>
      </>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Original Price</label>
            <div className="flex gap-2"><span className="self-center text-slate-500">$</span>
              <input type="number" step="0.01" value={original} onChange={e=>setOriginal(+e.target.value||0)} className="flex-1" data-testid="po-original"/>
            </div>
          </div>
          <div className="field"><label>Percent Off</label>
            <div className="flex gap-2">
              <input type="number" step="0.01" value={pct} onChange={e=>setPct(+e.target.value||0)} className="flex-1" data-testid="po-percent"/>
              <span className="self-center text-slate-500">%</span>
            </div>
          </div>
          <div className="mt-3">
            <div className="text-sm font-semibold text-slate-700 mb-2">Quick-pick:</div>
            <div className="flex flex-wrap gap-2">
              {chips.map(v => (
                <button key={v} onClick={()=>setPct(v)} data-testid={`po-chip-${v}`}
                  className={`px-3 py-1 rounded-full text-sm font-semibold border transition ${pct===v ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'}`}>
                  {v}% off
                </button>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="cnet-result-hdr">Result</div>
          <div className="cnet-result-body">
            <div className="mb-4">
              <div className="text-sm text-slate-500">Sale Price</div>
              <div className="text-3xl font-bold text-emerald-700" data-testid="po-sale">${c.sale.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b"><td className="py-2">Original Price</td><td className="text-right">{fmt(original)}</td></tr>
                <tr className="border-b"><td className="py-2">Percent Off</td><td className="text-right">{pct.toFixed(2)}%</td></tr>
                <tr className="border-b"><td className="py-2">You Save</td><td className="text-right text-red-600 font-semibold" data-testid="po-savings">−{fmt(c.savings)}</td></tr>
                <tr><td className="py-2 font-semibold">You Pay</td><td className="text-right font-bold text-emerald-700">{fmt(c.sale)}</td></tr>
              </tbody>
            </table>
            {/* Visual bar */}
            <div className="mt-4">
              <div className="h-6 w-full rounded overflow-hidden border border-slate-200 flex">
                <div style={{ width: `${Math.min(100, Math.max(0, 100 - pct))}%`, background: '#059669' }} className="flex items-center justify-center text-white text-xs font-semibold">
                  {(100 - pct).toFixed(0)}%
                </div>
                <div style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: '#dc2626' }} className="flex items-center justify-center text-white text-xs font-semibold">
                  {pct.toFixed(0)}%
                </div>
              </div>
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>You pay</span><span>You save</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reference table of common percent-off values */}
      <div className="mt-8">
        <h3 className="text-lg font-bold text-slate-900 mb-3">Common Discounts on ${original.toLocaleString()}</h3>
        <div className="overflow-auto border border-slate-200 rounded">
          <table className="w-full text-sm">
            <thead className="bg-blue-800 text-white"><tr><th className="px-3 py-2 text-left">Percent Off</th><th className="px-3 py-2 text-right">You Save</th><th className="px-3 py-2 text-right">Sale Price</th></tr></thead>
            <tbody>
              {[5,10,15,20,25,30,40,50,60,70,75,80,90].map((p,i)=>(
                <tr key={p} className={`${i%2?'bg-slate-50':''} ${p===Math.round(pct)?'bg-blue-50 font-semibold':''}`}>
                  <td className="px-3 py-1.5">{p}% off</td>
                  <td className="px-3 py-1.5 text-right text-red-600">{fmt(original * p / 100)}</td>
                  <td className="px-3 py-1.5 text-right text-emerald-700">{fmt(original * (1 - p/100))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <StepsPanel steps={[
        `Savings = Original × Percent Off = ${fmt(original)} × ${pct.toFixed(2)}% = <b>${fmt(c.savings)}</b>`,
        `Sale Price = Original − Savings = ${fmt(original)} − ${fmt(c.savings)} = <b>${fmt(c.sale)}</b>`,
      ]} />
    </CalcShell>
  );
}
